import { Typography, Flex, ColorPickerProps, Segmented, Space } from "antd";
import { Button, Tooltip } from "@libertify/ui";
import { PaletteResponse } from "pages/media/types/";
import React from "react";
import { EyeDropper } from "./EyeDropper";
import { AggregationColor } from "./colorTypes";
import type { Color } from "./colorTypes";
import { MainColorsSelect } from "./MainColorsSelect";
import { GradientDirectionButtons } from "./GradientDirectionButtons";
import type { GradientDirection } from "./types";
import { useTranslate } from "#src/hooks";
import { InformationIcon } from "../icons";

interface CustomColorPickerPanelProps {
  title?: string;
  allowClear?: boolean;
  pickerComponents: {
    components: {
      Picker: React.FC;
      Presets: React.FC;
    };
  };
  displayEyedropper: boolean;
  handleChangeComplete: (value: Color) => void;
  palette?: PaletteResponse;
  hasCustomPresets: boolean;
  colorMode: ColorPickerProps["mode"];
  setColorMode: (mode: ColorPickerProps["mode"]) => void;
  gradientDirection: GradientDirection;
  showGradient?: boolean;
  onGradientDirectionChange: (direction: GradientDirection) => void;
}

export const CustomColorPickerPanel = ({
  title,
  allowClear,
  pickerComponents,
  displayEyedropper,
  handleChangeComplete,
  palette,
  hasCustomPresets,
  colorMode,
  setColorMode,
  gradientDirection,
  showGradient = false,
  onGradientDirectionChange,
}: CustomColorPickerPanelProps) => {
  const t = useTranslate();
  const {
    components: { Picker, Presets },
  } = pickerComponents;
  // to avoide empty MainColorsSelect
  const paletteHasColors = Object.values(palette || {}).some(Boolean);
  return (
    <Flex vertical={!hasCustomPresets} gap={20} wrap="wrap">
      <Flex vertical gap={10} style={{ position: "relative", width: 180 }}>
        <ColorPickerPanelHeader
          allowClear={allowClear}
          title={title}
          displayEyedropper={displayEyedropper}
          handleChangeComplete={handleChangeComplete}
        />
        {showGradient && (
          <Segmented<ColorPickerProps["mode"]>
            size="small"
            options={[
              {
                value: "single",
                label: t("components.templates.settings.colorPicker.single"),
              },
              {
                value: "gradient",
                label: t("components.templates.settings.colorPicker.gradient"),
              },
            ]}
            value={colorMode}
            onChange={setColorMode}
            block
          />
        )}
        {colorMode === "gradient" && (
          <Flex align="center" justify="space-between" gap={4}>
            <GradientDirectionButtons
              value={gradientDirection}
              onChange={onGradientDirectionChange}
            />
            <Tooltip
              placement="top"
              trigger={"click"}
              content={
                <Space direction="vertical">
                  {t(
                    "components.templates.settings.colorPicker.gradientHintAdd"
                  )}
                  {t(
                    "components.templates.settings.colorPicker.gradientHintRemove"
                  )}
                </Space>
              }
            >
              <Button
                variant="tertiary"
                size="s"
                icon={<InformationIcon width={14} height={14} />}
              />
            </Tooltip>
          </Flex>
        )}
        <Picker />
      </Flex>
      <Flex vertical gap={6}>
        {palette && paletteHasColors && (
          <MainColorsSelect
            palette={palette}
            showCollapse={!hasCustomPresets}
            onColorClick={(color) =>
              handleChangeComplete(new AggregationColor(color))
            }
          />
        )}
        <Presets />
      </Flex>
    </Flex>
  );
};

const ColorPickerPanelHeader = ({
  allowClear = false,
  title,
  displayEyedropper,
  handleChangeComplete,
}: {
  allowClear?: boolean;
  title?: string;
  displayEyedropper: boolean;
  handleChangeComplete: (color: Color) => void;
}) => {
  return (
    <Flex style={{ marginInlineEnd: allowClear ? 32 : 0 }} justify="flex-end">
      {(title || allowClear) && (
        <Typography.Text type="secondary" style={{ width: "100%", height: 22 }}>
          {title}
        </Typography.Text>
      )}
      {displayEyedropper && (
        <EyeDropper handleChangeComplete={handleChangeComplete} />
      )}
    </Flex>
  );
};
